import Select from 'shared/components/select';
import styled, { css } from 'styled-components';

// Common colors for styled components
const COLORS = {
  text: '#717478',
  label: '#6d747b',
  border: '#d2d5da',
  borderHover: '#b6b6b6',
  background: '#fcfdfe',
  backgroundAlt: '#ecf0f3',
} as const;

// Base flex row component for reusability
const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const Container = styled.div`
  width: 548px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Row = styled(FlexRow)`
  justify-content: space-between;
`;

const SectionName = styled.p`
  font-size: 13px;
  font-weight: 700;
  color: ${COLORS.label};
  margin-bottom: 8px;
`;

const ScheduleFormRow = styled(Row)`
  & > *:first-child {
    margin-right: 8px;
  }

  * {
    flex-grow: 1;
  }
`;

const HoursSelectRow = styled(Row)`
  & > *:first-child {
    margin-right: 4px;
  }

  & > *:last-child {
    margin-left: 4px;
  }
`;

// Common input border styles for reusability
const inputBorderStyles = css`
  border: solid 1px ${COLORS.border};
  border-radius: 3px;

  &:focus {
    border-color: ${COLORS.borderHover};
  }
`;

type DatePickerProps = {
  disabled?: boolean;
};

const DatePicker = styled.div<DatePickerProps>`
  flex-grow: 1;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  input {
    color: ${COLORS.text};
    font-size: 13px;
    font-weight: normal;
    width: 142px;
    height: 45px;
    font-style: normal;
    ${inputBorderStyles}
    background-color: ${({ disabled }) => (disabled ? COLORS.backgroundAlt : COLORS.background)};
  }

  .DateInput_fang {
    margin-top: 1px;
    z-index: 501;
  }

  .SingleDatePicker_picker {
    z-index: 500;
  }

  .SingleDatePickerInput {
    display: flex;
    flex-grow: 1;
    width: 100%;
  }

  .SingleDatePicker,
  input.DateInput_input {
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    border: ${({ disabled }) => (disabled ? 'none' : '')};
    width: 100%;
  }

  td.CalendarDay__selected {
    background-color: #4597e0;
    border-color: #4597e0;
  }
`;

type StyledSelectProps = {
  timezone?: string;
  timezoneLeftPosition?: string;
  disabled?: boolean;
};

const StyledSelect = styled(Select)<StyledSelectProps>`
  background-color: ${({ disabled }) => (disabled ? COLORS.backgroundAlt : COLORS.background)};

  > select {
    border-color: ${COLORS.border};
    border-radius: 3px;
    color: ${COLORS.text};
    border: ${({ disabled }) => (disabled ? 'none' : '')};

    &:disabled {
      background-color: ${COLORS.backgroundAlt};
    }

    &:active,
    &:focus,
    &:hover {
      border-color: ${COLORS.borderHover};
      outline: none;
    }
  }

  &:before {
    color: ${({ theme }) => theme.colors.blueGrey[80]};
    content: '${(props) => props.timezone ?? ''}';
    font-size: 13px;
    position: absolute;
    top: calc(50% - 9px);
    left: ${(props) => props.timezoneLeftPosition ?? '75px'};
    width: max-content;
  }

  &:after {
    border-top: 5px solid ${COLORS.text} !important;
  }
`;

const NoEndDate = styled(Row)`
  justify-content: flex-start;
  margin-top: 12px;
`;

const DaysContainer = styled.div`
  gap: 8px;
  padding: 16px;
  border-radius: 4px;
  border: 1px solid ${COLORS.border};
`;

const DayRowContainer = styled(FlexRow)`
  height: 44px;
  align-items: center;
  margin-bottom: 8px;
`;

const ActiveSwitchWrapper = styled.div`
  margin-right: 8px;
`;

const DayName = styled.span`
  width: 72px;
  margin-right: 12px;
  color: ${COLORS.label};
  font-size: 13px;
  font-style: normal;
  font-weight: 700;
  text-transform: capitalize;
`;

const HoursContainer = styled(FlexRow)`
  width: 275px;
  margin-left: 12px;
  justify-content: space-around;
`;

const TwentyFourHours = styled(FlexRow)`
  width: 100%;
  padding: 12px 16px;
  justify-content: center;
  border-radius: 4px;
  background: ${COLORS.backgroundAlt};
`;

const MoreButtonContainer = styled(FlexRow)`
  margin-left: 8px;
  justify-content: center;
`;

export {
  Container,
  Row,
  SectionName,
  ScheduleFormRow,
  HoursSelectRow,
  DatePicker,
  StyledSelect,
  NoEndDate,
  DaysContainer,
  DayRowContainer,
  DayName,
  HoursContainer,
  ActiveSwitchWrapper,
  TwentyFourHours,
  MoreButtonContainer,
};
